import React from "react";
import { Box, FormControl, FormLabel, Stack, Textarea } from "@mui/joy";
import CommonModal from "../../common/CommonModal";
import DarkInput from "../../common/DarkInput";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, hrTextareaChromeSx } from "../../../styles/globalStyles";
import { AddCircleOutlineIcon, ImageIcon, SDelete } from "../../../styles/StyledMUI";
import type {
  Event,
  CreateEventRequest,
  UpdateEventRequest,
  Prize,
} from "../../../api/events/Event.typed";
import { calendarDateFromIsoUtc, endOfDayISO, startOfDayISO } from "../eventDateHelpers";

type Props = {
  open: boolean;
  onClose: () => void;
  event?: Event | null;
  onSubmit: (req: CreateEventRequest | UpdateEventRequest) => Promise<Event | null | undefined>;
  onUploadBackground?: (eventId: number, file: File) => Promise<unknown>;
};

const WEBP_MAX_WIDTH = 1440;
const WEBP_MAX_HEIGHT = 900;
const WEBP_QUALITY = 0.72;

/** Converts any image File to WebP via an offscreen canvas, resized to fit within WEBP_MAX_WIDTH × WEBP_MAX_HEIGHT. */
function convertToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectURL = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectURL);

      const ratio = Math.min(
        WEBP_MAX_WIDTH / img.naturalWidth,
        WEBP_MAX_HEIGHT / img.naturalHeight,
        1,
      );
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * ratio);
      canvas.height = Math.round(img.naturalHeight * ratio);
      canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("WebP conversion failed"));

            return;
          }

          const baseName = file.name.replace(/\.[^.]+$/, "");
          resolve(new File([blob], `${baseName}.webp`, { type: "image/webp" }));
        },
        "image/webp",
        WEBP_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectURL);
      reject(new Error("Image load failed"));
    };

    img.src = objectURL;
  });
}

const NAME_MAX = 100;
const DESC_MAX = 1500;
const PRIZE_DESC_MAX = 200;
const SLUG_MAX = 80;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const HOSTNAME_MAX = 253;
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** Strips diacritics, lowercases, collapses anything outside [a-z0-9] into single hyphens. */
function slugifyInput(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, SLUG_MAX);
}

const formLabelSx = { color: colorTokens.text.secondary, fontSize: "0.75rem" };

function CharCount({ value, max }: { value: string; max: number }) {
  const remaining = max - value.length;
  const warn = remaining <= Math.floor(max * 0.1);

  return (
    <DarkTypography
      level="body-xs"
      sx={{
        textAlign: "right",
        mt: 0.25,
        color: warn ? "var(--joy-palette-warning-400)" : colorTokens.text.dimmed,
      }}
    >
      {value.length} / {max}
    </DarkTypography>
  );
}

const bgPreviewSx = {
  display: "block",
  maxWidth: "100%",
  maxHeight: "140px",
  width: "auto",
  height: "auto",
  margin: "0 auto",
  borderRadius: "sm",
  objectFit: "contain" as const,
  border: `1px solid ${colorTokens.border.default}`,
};

export default function EventFormModal({
  open,
  onClose,
  event,
  onSubmit,
  onUploadBackground,
}: Props) {
  const isEdit = Boolean(event);
  const [name, setName] = React.useState(event?.name ?? "");
  const [description, setDescription] = React.useState(event?.description ?? "");
  const [startDate, setStartDate] = React.useState(
    event?.start_date ? calendarDateFromIsoUtc(event.start_date) : "",
  );
  const [endDate, setEndDate] = React.useState(
    event?.end_date ? calendarDateFromIsoUtc(event.end_date) : "",
  );
  const [drawDate, setDrawDate] = React.useState(
    event?.draw_date ? calendarDateFromIsoUtc(event.draw_date) : "",
  );
  const [slug, setSlug] = React.useState(event?.public_slug ?? "");
  const [customHostname, setCustomHostname] = React.useState(event?.custom_hostname ?? "");
  const [bgFile, setBgFile] = React.useState<File | null>(null);
  const [bgPreview, setBgPreview] = React.useState<string>("");
  const [prizes, setPrizes] = React.useState<Prize[]>(event?.prizes ?? []);
  const [submitting, setSubmitting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    setName(event?.name ?? "");
    setDescription(event?.description ?? "");
    setStartDate(event?.start_date ? calendarDateFromIsoUtc(event.start_date) : "");
    setEndDate(event?.end_date ? calendarDateFromIsoUtc(event.end_date) : "");
    setDrawDate(event?.draw_date ? calendarDateFromIsoUtc(event.draw_date) : "");
    setSlug(event?.public_slug ?? "");
    setCustomHostname(event?.custom_hostname ?? "");
    setPrizes(event?.prizes ?? []);
    setBgFile(null);
    setBgPreview("");
  }, [open, event]);

  const addPrize = () => {
    setPrizes((prev) => [
      ...prev,
      { rank: prev.length + 1, description: "", value: "", quantity: 1 },
    ]);
  };

  const updatePrize = (idx: number, field: keyof Prize, value: string | number) => {
    setPrizes((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const removePrize = (idx: number) => {
    setPrizes((prev) => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, rank: i + 1 })));
  };

  React.useEffect(() => {
    if (!bgFile) {
      setBgPreview("");
      return;
    }

    const url = URL.createObjectURL(bgFile);
    setBgPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [bgFile]);

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (endDate !== "" && value > endDate) {
      setEndDate(value);
    }
  };

  const slugValid =
    slug === "" || (slug.length >= 3 && slug.length <= SLUG_MAX && SLUG_RE.test(slug));
  const hostnameValid =
    customHostname === "" ||
    (customHostname.length <= HOSTNAME_MAX && HOSTNAME_RE.test(customHostname));
  const dateRangeValid = startDate !== "" && endDate !== "" && startDate <= endDate;
  const isValid = name.trim() !== "" && dateRangeValid && slugValid && hostnameValid;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0] ?? null;
    e.target.value = "";

    if (!raw) return;

    try {
      const webp = await convertToWebP(raw);
      setBgFile(webp);
    } catch {
      setBgFile(raw);
    }
  };

  const handleRemoveBg = () => setBgFile(null);

  const currentBgURL = bgPreview || event?.background_url || "";

  const handleConfirm = async () => {
    if (!isValid) return;

    setSubmitting(true);
    try {
      const cleanPrizes = prizes
        .map((p) => ({ ...p, value: p.value?.trim() || undefined }))
        .filter((p) => p.description.trim() !== "");

      const trimmedSlug = slug.trim();
      const slugChanged = trimmedSlug !== "" && trimmedSlug !== (event?.public_slug ?? "");

      const trimmedHostname = customHostname.trim().toLowerCase();
      const currentHostname = event?.custom_hostname ?? "";
      const hostnameChanged = trimmedHostname !== currentHostname;
      const hostnameCleared = hostnameChanged && trimmedHostname === "" && currentHostname !== "";

      const req: CreateEventRequest | UpdateEventRequest = isEdit
        ? {
            id: event!.id,
            name: name.trim(),
            description: description.trim() || undefined,
            start_date: startOfDayISO(startDate),
            end_date: endOfDayISO(endDate),
            draw_date: drawDate ? startOfDayISO(drawDate) : undefined,
            prizes: cleanPrizes,
            slug: slugChanged ? trimmedSlug : undefined,
            custom_hostname: hostnameChanged && !hostnameCleared ? trimmedHostname : undefined,
            clear_custom_hostname: hostnameCleared || undefined,
          }
        : {
            name: name.trim(),
            description: description.trim() || undefined,
            start_date: startOfDayISO(startDate),
            end_date: endOfDayISO(endDate),
            draw_date: drawDate ? startOfDayISO(drawDate) : undefined,
            prizes: cleanPrizes,
            slug: trimmedSlug || undefined,
            custom_hostname: trimmedHostname || undefined,
          };

      const saved = await onSubmit(req);

      if (saved && bgFile && onUploadBackground) {
        await onUploadBackground(saved.id, bgFile);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier l'événement" : "Nouvel événement"}
      onConfirm={handleConfirm}
      submitDisabled={!isValid}
      loading={submitting}
      size="xl"
    >
      <Stack spacing={2.5}>
        <FormControl required>
          <FormLabel sx={formLabelSx}>Nom</FormLabel>
          <DarkInput
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
            placeholder="Nom de l'événement"
            autoFocus
            slotProps={{ input: { maxLength: NAME_MAX } }}
          />
          <CharCount value={name} max={NAME_MAX} />
        </FormControl>
        <FormControl>
          <FormLabel sx={formLabelSx}>Domaine personnalisé (optionnel)</FormLabel>
          <DarkInput
            value={customHostname}
            onChange={(e) =>
              setCustomHostname(
                e.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9.-]/g, "")
                  .slice(0, HOSTNAME_MAX),
              )
            }
            placeholder=""
            slotProps={{
              input: {
                maxLength: HOSTNAME_MAX,
                inputMode: "url",
                autoCapitalize: "off",
                autoCorrect: "off",
                spellCheck: false,
              },
            }}
            error={customHostname !== "" && !hostnameValid}
          />
          {customHostname !== "" && !hostnameValid && (
            <DarkTypography
              level="body-xs"
              sx={{ color: "var(--joy-palette-danger-400)", mt: 0.25 }}
            >
              Format invalide. Exemple : concours.escapadeplongee.com
            </DarkTypography>
          )}
        </FormControl>

        <FormControl>
          <FormLabel sx={formLabelSx}>Slug (optionnel)</FormLabel>
          <DarkInput
            value={slug}
            onChange={(e) => setSlug(slugifyInput(e.target.value))}
            onBlur={() => setSlug((s) => s.replace(/-+$/, ""))}
            placeholder=""
            slotProps={{
              input: {
                maxLength: SLUG_MAX,
                inputMode: "url",
                autoCapitalize: "off",
                autoCorrect: "off",
                spellCheck: false,
              },
            }}
            error={slug !== "" && !slugValid}
          />
          {slug !== "" && !slugValid && (
            <DarkTypography
              level="body-xs"
              sx={{ color: "var(--joy-palette-danger-400)", mt: 0.25 }}
            >
              Minuscules, chiffres et tirets uniquement (3–80 caractères).
            </DarkTypography>
          )}
        </FormControl>
        <FormControl>
          <FormLabel sx={formLabelSx}>Description</FormLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, DESC_MAX))}
            placeholder="Description (optionnel)"
            minRows={4}
            maxRows={10}
            variant="plain"
            sx={hrTextareaChromeSx}
            slotProps={{ textarea: { maxLength: DESC_MAX } }}
          />
          <CharCount value={description} max={DESC_MAX} />
        </FormControl>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <FormControl required>
            <FormLabel sx={formLabelSx}>Date de début</FormLabel>
            <DarkInput
              type="date"
              value={startDate}
              slotProps={{ input: { max: endDate || undefined } }}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </FormControl>

          <FormControl required>
            <FormLabel sx={formLabelSx}>Date de fin</FormLabel>
            <DarkInput
              type="date"
              value={endDate}
              slotProps={{ input: { min: startDate || undefined } }}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormControl>
        </Box>

        {startDate !== "" && endDate !== "" && !dateRangeValid && (
          <DarkTypography
            level="body-sm"
            colorVariant="muted"
            sx={{ color: "var(--joy-palette-danger-400)" }}
          >
            La date de fin doit être postérieure à la date de début.
          </DarkTypography>
        )}

        <FormControl>
          <FormLabel sx={formLabelSx}>Date de tirage au sort</FormLabel>
          <DarkInput
            type="date"
            value={drawDate}
            slotProps={{ input: { min: endDate || undefined } }}
            onChange={(e) => setDrawDate(e.target.value)}
          />
        </FormControl>

        {/* Prizes section */}
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
          >
            <FormLabel sx={formLabelSx}>Lots à gagner</FormLabel>
            <IconButtonCTA
              size="sm"
              variant="plain"
              ctaVariant="accent"
              aria-label="Ajouter un lot"
              onClick={addPrize}
            >
              <AddCircleOutlineIcon sx={{ fontSize: "1rem" }} />
            </IconButtonCTA>
          </Box>
          <Stack spacing={1}>
            {prizes.map((prize, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                  background: colorTokens.bg.surface,
                  borderRadius: "sm",
                  p: 1,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: colorTokens.accent.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <DarkTypography
                    level="body-xs"
                    sx={{ fontWeight: 700, fontSize: "0.65rem", color: "#000", lineHeight: 1 }}
                  >
                    {prize.rank}
                  </DarkTypography>
                </Box>
                <DarkInput
                  size="sm"
                  value={prize.description}
                  onChange={(e) =>
                    updatePrize(idx, "description", e.target.value.slice(0, PRIZE_DESC_MAX))
                  }
                  placeholder="Description du lot"
                  slotProps={{ input: { maxLength: PRIZE_DESC_MAX } }}
                  sx={{ flex: "1 1 180px", minWidth: 0 }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <DarkInput
                    size="sm"
                    type="number"
                    value={prize.value ?? ""}
                    onChange={(e) => updatePrize(idx, "value", e.target.value)}
                    placeholder="0"
                    slotProps={{ input: { min: 0, step: 1 } }}
                    sx={{ width: "80px" }}
                  />
                  <DarkTypography level="body-xs" colorVariant="muted">
                    €
                  </DarkTypography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <DarkTypography level="body-xs" colorVariant="muted">
                    ×
                  </DarkTypography>
                  <DarkInput
                    size="sm"
                    type="number"
                    value={String(prize.quantity)}
                    onChange={(e) =>
                      updatePrize(idx, "quantity", Math.max(1, parseInt(e.target.value, 10) || 1))
                    }
                    slotProps={{ input: { min: 1 } }}
                    sx={{ width: "56px" }}
                  />
                </Box>
                <IconButtonCTA
                  size="sm"
                  variant="plain"
                  ctaVariant="danger"
                  aria-label="Supprimer ce lot"
                  onClick={() => removePrize(idx)}
                  sx={{ flexShrink: 0 }}
                >
                  <SDelete />
                </IconButtonCTA>
              </Box>
            ))}
            {prizes.length === 0 && (
              <DarkTypography level="body-xs" colorVariant="muted">
                Aucun lot configuré
              </DarkTypography>
            )}
          </Stack>
        </Box>

        {/* Background image section */}
        {onUploadBackground && (
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
            >
              <FormLabel sx={formLabelSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ImageIcon sx={{ fontSize: "0.9rem" }} />
                  Image de fond (page publique)
                </Box>
              </FormLabel>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {bgFile && (
                  <IconButtonCTA
                    size="sm"
                    variant="plain"
                    ctaVariant="danger"
                    aria-label="Retirer l'image sélectionnée"
                    onClick={handleRemoveBg}
                  >
                    <SDelete />
                  </IconButtonCTA>
                )}
                <IconButtonCTA
                  size="sm"
                  variant="plain"
                  ctaVariant="accent"
                  aria-label="Choisir une image de fond"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImageIcon sx={{ fontSize: "1rem" }} />
                </IconButtonCTA>
              </Box>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {currentBgURL ? (
              <img src={currentBgURL} alt="Aperçu fond" style={bgPreviewSx} />
            ) : (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  width: "100%",
                  height: "80px",
                  border: `1px dashed ${colorTokens.border.default}`,
                  borderRadius: "sm",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  opacity: 0.6,
                  "&:hover": { opacity: 1 },
                }}
              >
                <ImageIcon sx={{ fontSize: "1.2rem", color: colorTokens.text.secondary }} />
                <DarkTypography level="body-sm" colorVariant="muted">
                  Cliquer pour choisir une image
                </DarkTypography>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </CommonModal>
  );
}
